import { Dialog, DialogHeader, DialogBody, DialogFooter, Button } from '@material-tailwind/react';
import { FunctionComponent, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useApi } from '../../../providers/ApiProvider';
import { Devis, Demande } from '../../../types/AdminRessources';
import { GoodApiResponse, ErrorApiResponse } from '../../../types/api';
import { EntrepriseInfo } from '../../../types/auth';
import { handleError } from '../../../utils/ErrorHandler';
import { AlertError } from '../../Alertes';
import { InputFile } from '../../Inputs/InputFile';
import { InputText } from '../../Inputs/InputText';
import { InputTextarea } from '../../Inputs/InputTextarea';
import { LoadingAbsolute } from '../../LoadingAbsolute';
import { EntrepriseDisplay } from '../../EntrepriseDisplay';
import { UserDisplay } from '../../UserDisplay';
import { FormationSessionDisplay } from '../../FormationSessionDisplay';

/**
 * Ce composant permet d'afficher une modale pour que les admins puissent ajouter un devis.
 *
 * @param visible - Précise si la modale et ouverte ou non. Provient du parent.
 * @param setVisible - Accesseur qui modifie la visibilité de la modale. Provient du parent.
 * @param handleIndex - Callback qui réalise l'appel pour recharger les données annexes à la demande. Provient du parent.
 */
export const ModalAjouterDevisAdmin: FunctionComponent<{
  visible: boolean;
  setVisible: React.Dispatch<React.SetStateAction<boolean>>;
  handleIndex: () => void;
}> = ({ visible, setVisible, handleIndex }) => {
  /**
   * On stocke si on est en train d'utiliser l'API ou non.
   */
  const [apiIsLoading, setApiIsLoading] = useState(false);

  /**
   * Permet de donner un numéro au devis.
   */
  const [numero, setNumero] = useState<Devis['numero']>('');

  /**
   * Permet de donner un titre au document.
   */
  const [titre, setTitre] = useState<Devis['titre']>('');

  /**
   * Fichier à envoyer.
   */
  const [fichier, setFichier] = useState<Devis['fichier']>('');

  /**
   * Description du fichier, optionnel
   */
  const [description, setDescription] = useState<Devis['description']>('');

  /**
   * Liste des demandes
   */
  const [demandes, setDemandes] = useState<Demande[] | null>(null);

  /**
   * Demande sélectionnée
   */
  const [demandeId, setDemandeId] = useState<Demande['id'] | null>(null);

  /**
   * On stocke l'eventuelle erreur.
   */
  const [error, setError] = useState<string | null>(null);

  /**
   * On à besoin d'initialiser le client d'API à l'aide d'un hook personalisé.
   */
  const client = useApi();

  /**
   * Permet de réinitialiser les champs de la modale à chaque fois qu'on l'ouvre où qu'on la ferme.
   */
  useEffect(() => {
    setTitre('');
    setFichier('');
    setDescription('');
    setDemandeId(null);
    setError(null);
  }, [visible]);

  /**
   * Permet de rafraichir la liste des demandes, lorsque l'on change de profil.
   */
  useEffect(() => {
    if (visible) {
      handleListeDemandes();
    }
  }, [visible]);

  /**
   * Cette méthode permet d'obtenir la liste des demandes
   */
  const handleListeDemandes = () => {
    setApiIsLoading(true);
    client
      .get<GoodApiResponse<Demande[]>>('/admin/demandes')
      .then((response) => {
        setDemandes(response.data.data as unknown as (Demande & { id: number; entreprise: EntrepriseInfo })[]);
      })
      .catch((error: ErrorApiResponse) => {
        handleError(error);
      })
      .finally(() => {
        setApiIsLoading(false);
      });
  };

  /**
   * Envoie le fichier par l'API.
   *
   */
  const handleEnvoie = () => {
    setApiIsLoading(true);

    client
      .post<GoodApiResponse>(`/admin/devis`, {
        numero: numero,
        titre: titre,
        fichier: fichier,
        description: description,
        demande_id: demandeId,
      })
      .then(() => {
        setVisible(false);
        handleIndex();
        toast.success(`Envoi du devis réussi`);
      })
      .catch((error: ErrorApiResponse<Devis>) => {
        handleError(
          error,
          () => {
            setError(error.response.data.message);
          },
          () => {
            setError(error.message);
          },
        );
      })
      .finally(() => {
        setApiIsLoading(false);
      });
  };

  return (
    <Dialog open={visible} size='lg' handler={setVisible}>
      <DialogHeader>{`Ajouter un devis`}</DialogHeader>
      <DialogBody divider>
        {error && <AlertError text={error} />}
        <div className='flex flex-row items-center gap-3'>
          <div className='w-1/2'>
            <div>
              <p>Numéro</p>
            </div>
            <InputText value={numero} onChange={(e) => setNumero(e.target.value)} />
          </div>
          <div className='w-1/2'>
            <div>
              <p>Fichier :</p>
            </div>
            <InputFile
              onChange={(titre, base64) => {
                setTitre(titre);
                setFichier(base64);
                setError(null);
              }}
            />
          </div>
        </div>

        <div className='container mx-auto bg-white rounded-b-lg pt-4'>
          <div>
            <p>Demande liée (facultatif) :</p>
          </div>
          <div className='w-full overflow-x-auto overflow-y-auto border h-64'>
            <table className='table-fixed min-w-full bg-white rounded-b-lg'>
              <thead>
                <tr className='w-full h-12 border-gray-300 border-b py-8'>
                  <th className='py-4 text-secondary-1 px-4 text-left text-sm tracking-normal leading-4'>Numéro</th>
                  <th className='py-4 text-secondary-1 px-4 text-left text-sm tracking-normal leading-4'>Formation et session</th>
                  <th className='py-4 text-secondary-1 px-4 text-left text-sm tracking-normal leading-4'>Entreprise</th>
                  <th className='py-4 text-secondary-1 px-4 text-left text-sm tracking-normal leading-4'>Salarié</th>
                  <th className='py-4 text-secondary-1 px-4 text-left text-sm tracking-normal leading-4'></th>
                </tr>
              </thead>
              <tbody>
                <tr className='border-gray-100 border-b hover:bg-gray-100'>
                  <td colSpan={4} className='pl-4 py-3 text-gray-800 text-sm text-center'>
                    Aucune
                  </td>
                  <td className='px-4 py-3 text-gray-800 text-sm'>
                    <Button className='flex flex-row items-center rounded-full' size='sm' onClick={() => setDemandeId(null)} disabled={demandeId === null}>
                      Choisir
                    </Button>
                  </td>
                </tr>
                {demandes &&
                  demandes.map((demande) => (
                    <tr key={demande.id} className={`border-gray-100 border-b hover:bg-gray-100 ${demande.est_archive && 'bg-gray-200'}`}>
                      <td className='pl-4 py-3 text-gray-800 text-sm'>{demande.id?.toString().padStart(6, '0')}</td>
                      <td className='px-4 py-3'>
                        <FormationSessionDisplay formation={demande.formation} groupe={demande.groupe} />
                      </td>
                      <td className='px-4 py-3'>{demande.entreprise && <EntrepriseDisplay entreprise={demande.entreprise} />}</td>
                      <td className='px-4 py-3'>
                        <UserDisplay user={demande.user} />
                      </td>
                      <td className='px-4 py-3 text-gray-800 text-sm'>
                        <Button
                          className='flex flex-row items-center rounded-full'
                          size='sm'
                          onClick={() => setDemandeId(demande.id)}
                          disabled={demandeId === demande.id}>
                          Choisir
                        </Button>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className='pt-4'>
          <p>Description (facultatif)</p>
        </div>
        <InputTextarea value={description ? description : ''} onChange={(e) => setDescription(e.target.value)} />
      </DialogBody>
      <DialogFooter>
        <Button
          variant='text'
          color='gray'
          onClick={() => {
            setVisible(false);
          }}
          className='mr-1 !rounded-full'
          disabled={apiIsLoading}>
          Annuler
        </Button>
        <Button
          variant='filled'
          color='orange'
          onClick={() => {
            handleEnvoie();
          }}
          disabled={apiIsLoading}>
          Confirmer
        </Button>
      </DialogFooter>
      {apiIsLoading ? <LoadingAbsolute /> : ''}
    </Dialog>
  );
};
